import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { Box, IconButton, Typography, Tooltip } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { usePermissions } from '../../../../context/PermissionContext';
import { API_HEADERS } from '../../../../constants/apiHeaders';
import { PERMISSION_IDS } from '../../../../constants/permissions';
import api from '../../../../services/httpClient';
import { VACANCY_ENDPOINTS } from '../../../../services/endpoints/rec/vacancy.endpoints';
import { vacancyService } from '../../../../services/vacancy.service';
import Button from '../../../common/Button';
import Loading from '../../../common/Loading';
import SelectField from '../../../common/SelectField';
import TextField from '../../../common/TextField';
import PredefinedQuestionsModal from './PredefinedQuestionsModal';
import EditIcon from '../../../common/icons/EditIcon';
import CloseIcon from '../../../common/icons/CloseIcon';
import { ReactComponent as AddRegularQuestionIcon } from '../../../../assets/icons/Add Regular Question Button.svg';
import { ReactComponent as OpenPredefinedQuestionIcon } from '../../../../assets/icons/Open Predefined Question Button.svg';
import {
  levelQuestionsEqual,
  validateQuestionRowsForLevel,
} from '../../../../utils/vacancyValidation';

interface QuestionRow {
  _clientId: string;
  id: number | null;
  interviewQuestionId: number | null;
  question: string;
  answerType: 'number' | 'currency' | 'decimal' | 'text' | 'date' | 'boolean' | '';
  questionLevel: 0 | 1 | 5;
  applicantResponse: number;
  sortOrder: number;
  isActive: boolean;
  _source: 'normal' | 'predefined';
  _isExisting: boolean; // true if loaded from backend (has db id), false if newly added in UI
  _isEditing?: boolean; // true if this specific row is in edit mode
}

interface QuestionsErrorState {
  pageError: string | null;
  saveError: string | null;
  fieldErrors: Record<string, { question?: string; answerType?: string }>;
  apiErrors: { parentQuestions?: string; vacancyQuestions?: string; save?: string };
}

interface VacancyQuestionsFormProps {
  type: 'add' | 'edit';
  addedVacancyId?: number | null;
  onCancel: () => void;
  onSuccess: (message?: string) => void;
  onError: (message?: string) => void;
}

const ANSWER_TYPE_OPTIONS = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'currency', label: 'Currency' },
  { value: 'decimal', label: 'Decimal' },
  { value: 'date', label: 'Date' },
  { value: 'boolean', label: 'Boolean' },
];

const QUESTION_LEVELS = [
  { level: 0, label: 'Interview Questions' },
  { level: 1, label: 'First Interview' },
  { level: 5, label: 'Final Interview' },
] as const;

const VacancyQuestionsForm: React.FC<VacancyQuestionsFormProps> = ({
  type,
  addedVacancyId,
  onCancel,
  onSuccess,
  onError,
}) => {
  const params = useParams();
  const { hasPermission, pageId, setPageId } = usePermissions();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [savingLevel, setSavingLevel] = useState<0 | 1 | 5 | null>(null);
  const [questionsByLevel, setQuestionsByLevel] = useState<{
    0: QuestionRow[];
    1: QuestionRow[];
    5: QuestionRow[];
  }>({
    0: [],
    1: [],
    5: [],
  });
  // Saved: last version that matches the database (used when saving other levels)
  const [savedQuestionsByLevel, setSavedQuestionsByLevel] = useState<{
    0: QuestionRow[];
    1: QuestionRow[];
    5: QuestionRow[];
  }>({
    0: [],
    1: [],
    5: [],
  });
  const [errors, setErrors] = useState<QuestionsErrorState>({
    pageError: null,
    saveError: null,
    fieldErrors: {},
    apiErrors: {},
  });
  const [isPredefinedModalOpen, setIsPredefinedModalOpen] = useState(false);
  const [selectedLevel, setSelectedLevel] = useState<0 | 1 | 5 | null>(null);

  const vacancyId = addedVacancyId || params?.id;

  // Collect IDs of predefined questions already added to this vacancy
  const usedPredefinedQuestionIds: number[] = [0, 1, 5].flatMap((level) =>
    questionsByLevel[level as 0 | 1 | 5]
      .filter((q) => q._source === 'predefined' && q.interviewQuestionId != null)
      .map((q) => q.interviewQuestionId as number)
  );

  useEffect(() => {
    if ((type === 'add' && pageId !== 8) || (type === 'edit' && pageId !== 11)) {
      setPageId(type === 'add' ? 8 : 11);
      return;
    }

    if (type === 'add' || !vacancyId) return;

    const fetchQuestions = async () => {
      setIsLoading(true);
      try {
        const response = await vacancyService.getVacancyQuestions(Number(vacancyId));

        const questions = response.data.data || [];
        const grouped: { 0: QuestionRow[]; 1: QuestionRow[]; 5: QuestionRow[] } = {
          0: [],
          1: [],
          5: [],
        };

        questions.forEach((q) => {
          const row: QuestionRow = {
            _clientId: `${q.id}-${Date.now()}-${Math.random()}`,
            id: q.id,
            interviewQuestionId: q.interviewQuestionId,
            question: q.question,
            answerType: q.answerType,
            questionLevel: q.questionLevel,
            applicantResponse: q.applicantResponse,
            sortOrder: q.sortOrder,
            isActive: q.isActive,
            _source: q.interviewQuestionId ? 'predefined' : 'normal',
            _isExisting: true,
          };
          grouped[q.questionLevel].push(row);
        });

        // Sort by sortOrder within each level
        [0, 1, 5].forEach((level) => {
          grouped[level as 0 | 1 | 5].sort((a, b) => a.sortOrder - b.sortOrder);
        });

        // Set both draft and saved versions
        setQuestionsByLevel(grouped);
        setSavedQuestionsByLevel(JSON.parse(JSON.stringify(grouped))); // deep copy
      } catch (error: any) {
        console.error('Error fetching questions:', error);
        onError('Failed to load questions. Please try again.');
        setErrors((prev) => ({
          ...prev,
          apiErrors: { ...prev.apiErrors, vacancyQuestions: 'Failed to load questions' },
        }));
      } finally {
        setIsLoading(false);
      }
    };

    fetchQuestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, vacancyId, pageId, setPageId]);

  const addNormalQuestion = (level: 0 | 1 | 5) => {
    const newQuestion: QuestionRow = {
      _clientId: `${Date.now()}-${Math.random()}`,
      id: null,
      interviewQuestionId: null,
      question: '',
      answerType: '',
      questionLevel: level,
      applicantResponse: 0,
      sortOrder: questionsByLevel[level].length + 1,
      isActive: true,
      _source: 'normal',
      _isExisting: false, // Newly added, editable
    };

    setQuestionsByLevel((prev) => ({
      ...prev,
      [level]: [...prev[level], newQuestion],
    }));

    // Clear errors for this new row
    setErrors((prev) => ({
      ...prev,
      fieldErrors: {
        ...prev.fieldErrors,
        [newQuestion._clientId]: {},
      },
    }));
  };

  const openPredefinedModal = (level: 0 | 1 | 5) => {
    setSelectedLevel(level);
    setIsPredefinedModalOpen(true);
  };

  const handlePredefinedQuestionSelect = (predefinedQuestion: {
    id: number;
    question: string;
    questionLevel: 0 | 1 | 5;
    answerType: 'number' | 'currency' | 'decimal' | 'text' | 'date' | 'boolean';
  }) => {
    if (selectedLevel === null) return;

    const newQuestion: QuestionRow = {
      _clientId: `${Date.now()}-${Math.random()}`,
      id: null,
      interviewQuestionId: predefinedQuestion.id,
      question: predefinedQuestion.question,
      answerType: predefinedQuestion.answerType,
      questionLevel: selectedLevel,
      applicantResponse: 0,
      sortOrder: questionsByLevel[selectedLevel].length + 1,
      isActive: true,
      _source: 'predefined',
      _isExisting: false,
    };

    setQuestionsByLevel((prev) => ({
      ...prev,
      [selectedLevel]: [...prev[selectedLevel], newQuestion],
    }));

    setIsPredefinedModalOpen(false);
    setSelectedLevel(null);

    // Clear errors for this new row
    setErrors((prev) => ({
      ...prev,
      fieldErrors: {
        ...prev.fieldErrors,
        [newQuestion._clientId]: {},
      },
    }));
  };

  const updateQuestion = (clientId: string, field: keyof QuestionRow, value: any) => {
    setQuestionsByLevel((prev) => {
      // Only update the level that contains this question to avoid touching other levels
      for (const level of [0, 1, 5] as const) {
        const idx = prev[level].findIndex((q) => q._clientId === clientId);
        if (idx === -1) continue;
        const updatedLevel = prev[level].map((q) =>
          q._clientId === clientId ? { ...q, [field]: value } : q
        );
        return { ...prev, [level]: updatedLevel };
      }
      return prev;
    });

    // Clear field error when user edits
    setErrors((prev) => ({
      ...prev,
      fieldErrors: {
        ...prev.fieldErrors,
        [clientId]: {
          ...prev.fieldErrors[clientId],
          [field]: undefined,
        },
      },
    }));
  };

  const moveQuestion = (level: 0 | 1 | 5, fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) return;

    setQuestionsByLevel((prev) => {
      const questions = [...prev[level]];
      const [moved] = questions.splice(fromIndex, 1);
      questions.splice(toIndex, 0, moved);

      // Recompute sortOrder
      const updated = questions.map((q, idx) => ({
        ...q,
        sortOrder: idx + 1,
      }));

      return {
        ...prev,
        [level]: updated,
      };
    });
  };

  const deleteQuestion = async (clientId: string) => {
    // Find the question and which level it belongs to
    let questionToDelete: QuestionRow | undefined = undefined;
    let levelOfQuestion: 0 | 1 | 5 | null = null;
    for (const level of [0, 1, 5] as const) {
      const found = questionsByLevel[level].find((q) => q._clientId === clientId);
      if (found) {
        questionToDelete = found;
        levelOfQuestion = level;
        break;
      }
    }

    if (!questionToDelete || levelOfQuestion === null) return;
    const level: 0 | 1 | 5 = levelOfQuestion;

    // If it's a new question (not saved yet), just remove from state — only update the level that had the question.
    // Keep original sortOrder on remaining questions so draft matches saved and buttons disappear (add-then-remove = no change).
    if (!questionToDelete.id) {
      setQuestionsByLevel((prev) => {
        const filtered = prev[level].filter((q) => q._clientId !== clientId);
        return {
          ...prev,
          [level]: filtered,
        };
      });

      // Remove any validation errors for this row
      setErrors((prev) => {
        const { [clientId]: _removed, ...rest } = prev.fieldErrors;
        return {
          ...prev,
          fieldErrors: rest,
        };
      });
      return;
    }

    // For existing questions, call the DELETE API
    const questionId = questionToDelete.id;
    if (!questionId) return; // Extra safety check

    try {
      await api.delete(VACANCY_ENDPOINTS.deleteQuestion(questionId), {
        headers: { ...API_HEADERS.VACANCY_INTERVIEW_QUESTIONS_DELETE },
      });

      // If successful, remove from state — only update the level that had the question
      setQuestionsByLevel((prev) => {
        const filtered = prev[level].filter((q) => q._clientId !== clientId);
        const updatedLevel = filtered.map((q, idx) => ({
          ...q,
          sortOrder: idx + 1,
        }));
        return {
          ...prev,
          [level]: updatedLevel,
        };
      });

      // Also update saved snapshot for this level only to keep in sync with DB
      setSavedQuestionsByLevel((prev) => {
        const filtered = prev[level].filter((q) => q._clientId !== clientId);
        const updatedLevel = filtered.map((q, idx) => ({
          ...q,
          sortOrder: idx + 1,
        }));
        return {
          ...prev,
          [level]: updatedLevel,
        };
      });

      // Remove any validation errors for this row
      setErrors((prev) => {
        const { [clientId]: _removed, ...rest } = prev.fieldErrors;
        return {
          ...prev,
          fieldErrors: rest,
        };
      });

      onSuccess('Question deleted successfully!');
    } catch (error: any) {
      console.error('Error deleting question:', error);
      const errorMessage =
        error.response?.data?.message || 'Failed to delete question. Please try again.';
      onError(errorMessage);
    }
  };

  const validateQuestions = (level: 0 | 1 | 5): boolean => {
    const { fieldErrors: newFieldErrors, hasErrors } = validateQuestionRowsForLevel(
      questionsByLevel[level],
    );

    setErrors((prev) => ({
      ...prev,
      fieldErrors: newFieldErrors,
      saveError: hasErrors ? 'Please fix the errors before saving.' : null,
    }));

    return !hasErrors;
  };

  const handleDiscardLevel = (level: 0 | 1 | 5) => {
    // Revert the level back to the last saved state (closes edit modes and removes unsaved changes)
    setQuestionsByLevel((prev) => ({
      ...prev,
      [level]: JSON.parse(JSON.stringify(savedQuestionsByLevel[level])),
    }));

    // Clear any validation errors for this level
    setErrors((prev) => {
      const newFieldErrors = { ...prev.fieldErrors };
      questionsByLevel[level].forEach((q) => {
        delete newFieldErrors[q._clientId];
      });
      return {
        ...prev,
        fieldErrors: newFieldErrors,
        saveError: null,
      };
    });
  };

  const handleSaveLevel = async (level: 0 | 1 | 5) => {
    // Validate only the level being saved
    if (!validateQuestions(level)) {
      onError('Please fix the errors before saving.');
      return;
    }

    if (!vacancyId) {
      onError('Vacancy ID is required.');
      return;
    }

    setIsSaving(true);
    setSavingLevel(level);
    setErrors((prev) => ({ ...prev, saveError: null }));

    try {
      // Build payload: draft version of current level + saved versions of other levels
      const allQuestions: QuestionRow[] = [];
      
      [0, 1, 5].forEach((lvl) => {
        const typedLevel = lvl as 0 | 1 | 5;
        // Use draft for the level being saved, saved for others
        const questionsForLevel = typedLevel === level 
          ? questionsByLevel[typedLevel] 
          : savedQuestionsByLevel[typedLevel];
          
        questionsForLevel.forEach((q, idx) => {
          allQuestions.push({
            ...q,
            sortOrder: idx + 1,
          });
        });
      });

      const payload = {
        questions: allQuestions.map((q) => ({
          id: q.id,
          interviewQuestionId: q.interviewQuestionId,
          question: q.question.trim(),
          questionLevel: q.questionLevel,
          answerType: q.answerType,
          sortOrder: q.sortOrder,
          isActive: q.isActive,
        })),
      };

      await api.post(VACANCY_ENDPOINTS.updateQuestions(vacancyId), payload, {
        headers: { ...API_HEADERS.VACANCY_INTERVIEW_QUESTIONS_SAVE },
      });

      // Reload only the saved level from backend to sync IDs and DB state
      const response = await vacancyService.getVacancyQuestionsByLevel(Number(vacancyId), level);

      const levelQuestions = response.data.data || [];

      const mappedLevelQuestions: QuestionRow[] = levelQuestions.map((q) => ({
        _clientId: `${q.id}-${Date.now()}-${Math.random()}`,
        id: q.id,
        interviewQuestionId: q.interviewQuestionId,
        question: q.question,
        answerType: q.answerType,
        questionLevel: q.questionLevel,
        applicantResponse: q.applicantResponse,
        sortOrder: q.sortOrder,
        isActive: q.isActive,
        _source: q.interviewQuestionId ? 'predefined' : 'normal',
        _isExisting: true,
      }));

      mappedLevelQuestions.sort((a, b) => a.sortOrder - b.sortOrder);

      // Update both draft and saved for the saved level ONLY.
      // Do not touch other levels so their unsaved edits remain intact.
      setQuestionsByLevel((prev) => ({
        ...prev,
        [level]: mappedLevelQuestions,
      }));

      setSavedQuestionsByLevel((prev) => ({
        ...prev,
        [level]: JSON.parse(JSON.stringify(mappedLevelQuestions)),
      }));

      onSuccess('Questions saved successfully!');
    } catch (error: any) {
      console.error('Error saving questions:', error);
      const errorMessage =
        error.response?.data?.message || 'Failed to save questions. Please try again.';
      onError(errorMessage);
      setErrors((prev) => ({
        ...prev,
        saveError: errorMessage,
        apiErrors: { ...prev.apiErrors, save: errorMessage },
      }));
    } finally {
      setIsSaving(false);
      setSavingLevel(null);
    }
  };

  const hasChanges = (level: 0 | 1 | 5): boolean => {
    const draftQuestions = questionsByLevel[level];
    const savedQuestions = savedQuestionsByLevel[level];
    return !levelQuestionsEqual(draftQuestions, savedQuestions);
  };

  const hasEditingInLevel = (level: 0 | 1 | 5): boolean => {
    return questionsByLevel[level].some((q) => q._isEditing === true);
  };

  const renderQuestionSection = (level: 0 | 1 | 5, label: string) => {
    const questions = questionsByLevel[level];

    return (
      <Box className="edit-section" sx={{ mb: 0.5 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 0.5,
            width: '1200px',
          }}
        >
          <Typography className="edit-section-title">{label}</Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Tooltip title="Add normal questions">
            <IconButton
              onClick={() => addNormalQuestion(level)}
              sx={{
                p: 0,
                borderRadius: 0,
                backgroundColor: 'transparent',
                '&:hover': {
                  backgroundColor: 'transparent',
                  opacity: 0.8,
                },
                '& svg': {
                  width: '100%',
                  height: '100%',
                  display: 'block',
                },
              }}
            >
              <AddRegularQuestionIcon />
            </IconButton>
            </Tooltip>
            <Tooltip title="Add predefined questions">
            <IconButton
              onClick={() => openPredefinedModal(level)}
              sx={{
                p: 0,
                borderRadius: 0,
                backgroundColor: 'transparent',
                '&:hover': {
                  backgroundColor: 'transparent',
                  opacity: 0.8,
                },
                '& svg': {
                  width: '100%',
                  height: '100%',
                  display: 'block',
                },
              }}
            >
              <OpenPredefinedQuestionIcon />
            </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {questions.map((question, index) => (
          <Box
            key={question._clientId}
            className="edit-sub-section"
            sx={{ width: '1200px', mb: 1, position: 'relative' }}
          >
            {/* Delete Icon - always visible */}
            <IconButton
              onClick={() => deleteQuestion(question._clientId)}
              sx={{
                position: 'absolute',
                top: '13px',
                right: question._isExisting && !question._isEditing ? '40px' : '8px',
                zIndex: 1,
                padding: '2px',
                '& svg': {
                  fontSize: 20,
                },
                '& svg path': {
                  fill: '#E60e0E',
                },
                '& svg circle': {
                  fill: '#E9584526',
                },
              }}
            >
              <CloseIcon />
            </IconButton>

            {/* Edit Icon - only for existing questions that are not in edit mode */}
            {question._isExisting && !question._isEditing && (
              <IconButton
                className="view-edit-icon"
                onClick={() => updateQuestion(question._clientId, '_isEditing', true)}
                sx={{ position: 'absolute', top: '8px', right: '8px', zIndex: 1 }}
              >
                <EditIcon />
              </IconButton>
            )}

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'center', mt: 1 }}>
                <IconButton
                  size="small"
                  disabled={index === 0 || (question._isExisting && !question._isEditing)}
                  onClick={() => moveQuestion(level, index, index - 1)}
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    border: '1px solid #D0D5DD',
                    backgroundColor: '#FFFFFF',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '& .MuiSvgIcon-root': {
                      fontSize: 16,
                      color: '#667085',
                    },
                    '&:hover': {
                      backgroundColor: '#F9FAFB',
                    },
                    '&.Mui-disabled': {
                      backgroundColor: '#F2F4F7',
                      borderColor: '#E4E7EC',
                      '& .MuiSvgIcon-root': {
                        color: '#D0D5DD',
                      },
                    },
                  }}
                >
                  <ArrowUpwardIcon />
                </IconButton>
                <IconButton
                  size="small"
                  disabled={index === questions.length - 1 || (question._isExisting && !question._isEditing)}
                  onClick={() => moveQuestion(level, index, index + 1)}
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    border: '1px solid #D0D5DD',
                    backgroundColor: '#FFFFFF',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '& .MuiSvgIcon-root': {
                      fontSize: 16,
                      color: '#667085',
                    },
                    '&:hover': {
                      backgroundColor: '#F9FAFB',
                    },
                    '&.Mui-disabled': {
                      backgroundColor: '#F2F4F7',
                      borderColor: '#E4E7EC',
                      '& .MuiSvgIcon-root': {
                        color: '#D0D5DD',
                      },
                    },
                  }}
                >
                  <ArrowDownwardIcon />
                </IconButton>
              </Box>
              <Box sx={{ flex: 1, display: 'flex', gap: 7 }}>
                <Box sx={{ flex: 1 }}>
                  <TextField
                    label={question._source === 'predefined' ? 'Question (P)' : 'Question (N)'}
                    multiline
                    rows={3}
                    value={question.question}
                    onChange={(e) => updateQuestion(question._clientId, 'question', e.target.value)}
                    error={!!errors.fieldErrors[question._clientId]?.question}
                    helperText={errors.fieldErrors[question._clientId]?.question}
                    required
                    disabled={question._isExisting && !question._isEditing}
                    sx={{
                      width: '100%',
                      '& .MuiOutlinedInput-root': {
                        height: 'auto',
                        alignItems: 'flex-start',
                        padding: 0,
                      },
                      '& .MuiOutlinedInput-input': {
                        padding: '8px 12px',
                      },
                      '& .MuiOutlinedInput-inputMultiline': {
                        padding: '8px 12px',
                      },
                      '& .MuiFormHelperText-root': {
                        marginTop: '2px',
                        marginBottom: 0,
                      },
                    }}
                  />
                </Box>
                <Box>
                  <SelectField
                    label="Answer Type"
                    width="360px"
                    options={ANSWER_TYPE_OPTIONS}
                    value={question.answerType}
                    onChange={(e) =>
                      updateQuestion(question._clientId, 'answerType', e.target.value)
                    }
                    disabled={question._source === 'predefined' || (question._isExisting && !question._isEditing)}
                    error={!!errors.fieldErrors[question._clientId]?.answerType}
                    helperText={errors.fieldErrors[question._clientId]?.answerType}
                    required={question._source === 'normal'}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        ))}

        {questions.length === 0 && (
          <Box sx={{ py: 2, color: '#667085', fontSize: '14px' }}>
            No questions added yet.
          </Box>
        )}

        {/* Per-level Save Button - aligned so Save button right edge matches 'Add predefined questions' icon */}
        {((type === 'add' && hasPermission(PERMISSION_IDS.VACANCY_QUESTIONS_SAVE_ADD)) || (type === 'edit' && hasPermission(PERMISSION_IDS.VACANCY_QUESTIONS_SAVE_EDIT))) && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2, mb: 2, width: '1200px' }}>
            <Button
              variant="outlined"
              onClick={() => handleDiscardLevel(level)}
              className="edit-section-cancel-button"
              disabled={isSaving || (!hasChanges(level) && !hasEditingInLevel(level))}
              width="150px"
            >
              Discard
            </Button>
            <Button
              variant="contained"
              onClick={() => handleSaveLevel(level)}
              isLoading={isSaving && savingLevel === level}
              className="edit-section-save-button"
              disabled={isSaving || !hasChanges(level)}
              width="150px"
            >
              {isSaving && savingLevel === level ? 'Saving...' : `Save`}
            </Button>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box gap={3} display="flex" flexDirection="column">
      {isLoading ? (
        <Loading />
      ) : (
        <>
          {errors.pageError && (
            <Box sx={{ color: 'error.main', mb: 2 }}>{errors.pageError}</Box>
          )}
          {errors.saveError && (
            <Box sx={{ color: 'error.main', mb: 2 }}>{errors.saveError}</Box>
          )}

          {QUESTION_LEVELS.map(({ level, label }) => renderQuestionSection(level, label))}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
            <Button variant="outlined" onClick={onCancel} className="edit-section-cancel-button">
              Back
            </Button>
          </Box>
        </>
      )}

      <PredefinedQuestionsModal
        open={isPredefinedModalOpen}
        onClose={() => {
          setIsPredefinedModalOpen(false);
          setSelectedLevel(null);
        }}
        onSelect={handlePredefinedQuestionSelect}
        filterLevel={selectedLevel}
        usedQuestionIds={usedPredefinedQuestionIds}
      />
    </Box>
  );
};

export default VacancyQuestionsForm;